import { useState, useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { itemsApi } from '@/services/itemsApi';
import { categoryApi, unitApi, gstRateApi, brandApi, modelApi } from '@/services/masterDataApi';
import { WARRANTY_TYPE_OPTIONS, getWarrantyDaysFromType } from '@/services/warrantyApi';
import { X, Package, Loader2, Edit2, Shield } from 'lucide-react';
import { Item } from '@/types';
import { formatCurrency, getCurrencySymbol } from '@/utils/currency';
import BeforeAfterTaxPriceField from '@/components/items/BeforeAfterTaxPriceField';
import SimpleSelect from '@/components/common/SimpleSelect';

interface EditItemModalProps {
  isOpen: boolean;
  onClose: () => void;
  item: Item;
}

interface ItemFormData {
  itemName: string;
  barcode?: string;
  description?: string;
  modelVariant?: string;
  brandId?: string;
  modelId?: string;
  categoryId?: string;
  unitId?: string;
  purchasePrice?: number;
  salesPrice?: number;
  purchasePriceIncludesTax?: boolean;
  salesPriceIncludesTax?: boolean;
  hsnCode?: string;
  gstRateId?: string;
  taxType?: 'CGST_SGST' | 'IGST';
  warrantyType?: string;
  warrantyDays?: number;
}

const validateHSNCode = (code: string) => {
  const cleaned = code.replace(/\s/g, '');
  return /^\d{6}$|^\d{8}$/.test(cleaned);
};

export default function EditItemModal({
  isOpen,
  onClose,
  item,
}: EditItemModalProps) {
  const queryClient = useQueryClient();
  const [error, setError] = useState('');

  const [formData, setFormData] = useState<ItemFormData>({
    itemName: '',
    barcode: '',
    description: '',
    modelVariant: '',
    purchasePrice: undefined,
    salesPrice: undefined,
    hsnCode: '',
    taxType: 'CGST_SGST',
    warrantyType: 'NONE',
    warrantyDays: 0,
  });

  // Initialize form data with item values
  useEffect(() => {
    if (item) {
      setFormData({
        itemName: item.itemName,
        barcode: item.barcode || '',
        description: item.description || '',
        modelVariant: item.modelVariant || '',
        brandId: item.brandId || undefined,
        modelId: item.modelId || undefined,
        categoryId: item.categoryId || undefined,
        unitId: item.unitId || undefined,
        purchasePrice: item.purchasePrice || undefined,
        salesPrice: item.salesPrice || undefined,
        purchasePriceIncludesTax: item.purchasePriceIncludesTax || false,
        salesPriceIncludesTax: item.salesPriceIncludesTax || false,
        hsnCode: item.hsnCode || '',
        gstRateId: item.gstRateId || undefined,
        taxType: item.taxType || 'CGST_SGST',
        warrantyType: item.warrantyType || 'NONE',
        warrantyDays: item.warrantyDays || 0,
      });
    }
  }, [item]);

  // Fetch master data from API
  const { data: categoriesData } = useQuery({
    queryKey: ['categories-dropdown'],
    queryFn: () => categoryApi.getAll({ limit: 100, isActive: true }),
  });

  const { data: unitsData } = useQuery({
    queryKey: ['units-dropdown'],
    queryFn: () => unitApi.getAll({ limit: 100, isActive: true }),
  });

  const { data: gstRatesData } = useQuery({
    queryKey: ['gst-rates-dropdown'],
    queryFn: () => gstRateApi.getAll({ limit: 100, isActive: true }),
  });

  const { data: brandsData } = useQuery({
    queryKey: ['brands-dropdown'],
    queryFn: () => brandApi.getAll({ limit: 100, isActive: true }),
  });

  const { data: modelsData } = useQuery({
    queryKey: ['models-dropdown', formData.brandId],
    queryFn: () => modelApi.getAll({
      limit: 100,
      isActive: true,
      brandId: formData.brandId
    }),
    enabled: !!formData.brandId,
  });

  const updateMutation = useMutation({
    mutationFn: (data: ItemFormData) => itemsApi.updateItem(item.id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['items'] });
      alert('Item updated successfully');
      onClose();
    },
    onError: (error: any) => {
      setError(error.response?.data?.message || 'Failed to update item');
    },
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Validation
    if (!formData.itemName.trim()) {
      setError('Item name is required');
      return;
    }

    if (formData.hsnCode && !validateHSNCode(formData.hsnCode)) {
      setError('HSN code must be 6 or 8 digits');
      return;
    }

    if (!formData.unitId) {
      setError('Unit is required');
      return;
    }

    if (formData.purchasePrice !== undefined && formData.purchasePrice < 0) {
      setError('Purchase price cannot be negative');
      return;
    }

    if (formData.salesPrice !== undefined && formData.salesPrice < 0) {
      setError('Sales price cannot be negative');
      return;
    }

    updateMutation.mutate(formData);
  };

  // Calculate GST for display
  const gstRate = gstRatesData?.data.find(r => r.id === formData.gstRateId);
  const gstPercentage = gstRate?.rate || 0;
  const gstAmount = formData.salesPrice ? (formData.salesPrice * gstPercentage) / 100 : 0;
  const margin = (formData.salesPrice || 0) - (formData.purchasePrice || 0);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-yellow-100 rounded-lg flex items-center justify-center">
              <Edit2 className="h-6 w-6 text-yellow-600" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900">Edit Item</h2>
              <p className="text-sm text-gray-600">Update item in the company catalog</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="h-6 w-6 text-gray-500" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}

          {/* Item Code Display */}
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
            <div className="text-sm text-gray-600">Item Code</div>
            <div className="font-semibold text-gray-900">{item.itemCode}</div>
          </div>

          {/* Basic Information */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Basic Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Item Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.itemName}
                  onChange={(e) => setFormData({ ...formData, itemName: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Barcode
                </label>
                <input
                  type="text"
                  value={formData.barcode || ''}
                  onChange={(e) => setFormData({ ...formData, barcode: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Scan or enter barcode"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Category
                </label>
                <SimpleSelect
                  value={formData.categoryId || ''}
                  onChange={(v) => setFormData({ ...formData, categoryId: v || undefined })}
                  options={[
                    { value: '', label: 'Select Category' },
                    ...(categoriesData?.data ?? []).map((cat) => ({ value: cat.id, label: cat.name })),
                  ]}
                  placeholder="Select Category"
                  aria-label="Category"
                  wrapperClassName="w-full min-w-0"
                  className="focus:!ring-blue-500 focus:!border-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Brand</label>
                <SimpleSelect
                  value={formData.brandId || ''}
                  onChange={(v) => setFormData({
                    ...formData,
                    brandId: v || undefined,
                    modelId: undefined, // Reset model when brand changes
                  })}
                  options={[
                    { value: '', label: 'Select Brand' },
                    ...(brandsData?.data ?? []).map((brand) => ({ value: brand.id, label: brand.name })),
                  ]}
                  placeholder="Select Brand"
                  aria-label="Brand"
                  wrapperClassName="w-full min-w-0"
                  className="focus:!ring-blue-500 focus:!border-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Model</label>
                <SimpleSelect
                  value={formData.modelId || ''}
                  onChange={(v) => setFormData({ ...formData, modelId: v || undefined })}
                  disabled={!formData.brandId}
                  options={[
                    { value: '', label: 'Select Model' },
                    ...(modelsData?.data ?? []).map((model) => ({ value: model.id, label: model.name })),
                  ]}
                  placeholder="Select Model"
                  aria-label="Model"
                  wrapperClassName="w-full min-w-0"
                  className="focus:!ring-blue-500 focus:!border-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Model/Variant</label>
                <input
                  type="text"
                  value={formData.modelVariant || ''}
                  onChange={(e) => setFormData({ ...formData, modelVariant: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="e.g., 128GB, Black"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Unit <span className="text-red-500">*</span>
                </label>
                <SimpleSelect
                  value={formData.unitId || ''}
                  onChange={(v) => setFormData({ ...formData, unitId: v || undefined })}
                  options={[
                    { value: '', label: 'Select Unit' },
                    ...(unitsData?.data ?? []).map((unit) => ({
                      value: unit.id,
                      label: `${unit.name} (${unit.code})${unit.symbol ? ` - ${unit.symbol}` : ''}`,
                    })),
                  ]}
                  placeholder="Select Unit"
                  aria-label="Unit"
                  wrapperClassName="w-full min-w-0"
                  className="focus:!ring-blue-500 focus:!border-blue-500"
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                <textarea
                  value={formData.description || ''}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={3}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Additional details about this item"
                />
              </div>
            </div>
          </div>

          {/* Pricing & GST */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Pricing & GST</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <BeforeAfterTaxPriceField
                  label="Purchase Price"
                  baseValue={formData.purchasePrice}
                  currencySymbol={getCurrencySymbol()}
                  gstRateId={formData.gstRateId}
                  gstRateOptions={gstRatesData?.data || []}
                  onRateChange={(id) => setFormData({ ...formData, gstRateId: id })}
                  onChange={(value, includesTax) => setFormData({ ...formData, purchasePrice: value, purchasePriceIncludesTax: includesTax })}
                />
              </div>

              <div className="md:col-span-2">
                <BeforeAfterTaxPriceField
                  label="Sales Price"
                  baseValue={formData.salesPrice}
                  currencySymbol={getCurrencySymbol()}
                  gstRateId={formData.gstRateId}
                  gstRateOptions={gstRatesData?.data || []}
                  onRateChange={(id) => setFormData({ ...formData, gstRateId: id })}
                  onChange={(value, includesTax) => setFormData({ ...formData, salesPrice: value, salesPriceIncludesTax: includesTax })}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  HSN Code
                </label>
                <input
                  type="text"
                  value={formData.hsnCode || ''}
                  onChange={(e) => setFormData({ ...formData, hsnCode: e.target.value })}
                  placeholder="6 or 8 digits"
                  maxLength={8}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Tax Type
                </label>
                <div className="flex gap-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="taxType"
                      value="CGST_SGST"
                      checked={formData.taxType === 'CGST_SGST'}
                      onChange={(e) => setFormData({ ...formData, taxType: e.target.value as any })}
                      className="w-4 h-4 text-blue-600 focus:ring-2 focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700">CGST + SGST (Intra-state)</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="taxType"
                      value="IGST"
                      checked={formData.taxType === 'IGST'}
                      onChange={(e) => setFormData({ ...formData, taxType: e.target.value as any })}
                      className="w-4 h-4 text-blue-600 focus:ring-2 focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700">IGST (Inter-state)</span>
                  </label>
                </div>
              </div>

              {/* GST Calculation Display */}
              {formData.salesPrice && formData.salesPrice > 0 && gstRate && (
                <div className="md:col-span-2 bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h4 className="text-sm font-semibold text-blue-900 mb-2">GST Calculation Preview</h4>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <div className="text-blue-600">GST ({gstPercentage}%)</div>
                      <div className="font-semibold text-blue-900">{formatCurrency(gstAmount)}</div>
                    </div>
                    <div>
                      <div className="text-blue-600">Margin</div>
                      <div className={`font-semibold ${margin >= 0 ? 'text-green-900' : 'text-red-900'}`}>
                        {formatCurrency(margin)}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Warranty */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Shield className="h-5 w-5 text-green-600" />
              Warranty
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Warranty Period
                </label>
                <SimpleSelect
                  value={formData.warrantyType || 'NONE'}
                  onChange={(type) => {
                    const days = getWarrantyDaysFromType(type);
                    setFormData({
                      ...formData,
                      warrantyType: type,
                      warrantyDays: type === 'CUSTOM' ? (formData.warrantyDays || 0) : days,
                    });
                  }}
                  options={WARRANTY_TYPE_OPTIONS.map((o) => ({ value: String(o.value), label: o.label }))}
                  aria-label="Warranty type"
                  wrapperClassName="w-full min-w-0"
                  className="focus:!ring-blue-500 focus:!border-blue-500"
                />
              </div>

              {formData.warrantyType === 'CUSTOM' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Custom Warranty Days
                  </label>
                  <input
                    type="number"
                    min="1"
                    value={formData.warrantyDays || ''}
                    onChange={(e) => setFormData({
                      ...formData,
                      warrantyDays: e.target.value ? parseInt(e.target.value) : 0
                    })}
                    placeholder="Enter number of days"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              )}

              {formData.warrantyType && formData.warrantyType !== 'NONE' && formData.warrantyDays && formData.warrantyDays > 0 && (
                <div className="md:col-span-2 bg-green-50 border border-green-200 rounded-lg p-4">
                  <div className="flex items-center gap-2 text-green-800">
                    <Shield className="h-5 w-5" />
                    <span className="font-medium">
                      This item will have a {formData.warrantyDays} day warranty from delivery date
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={updateMutation.isPending}
              className="flex items-center gap-2 px-6 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {updateMutation.isPending ? (
                <>
                  <Loader2 className="h-5 w-5 animate-spin" />
                  Updating...
                </>
              ) : (
                <>
                  <Edit2 className="h-5 w-5" />
                  Update Item
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
